import React, { useEffect, useRef, useState } from 'react';
import { generateMonthGrid, resolveCellState, resolveKeyboardTarget, NavKey } from '../../model/datePicker/calendarGrid';
import { monthOf, addMonthsClamped } from '../../model/datePicker/dateHelpers';
import { LocalDate, YearMonth, DateRange } from '../../model/datePicker/types';
import { formatCellAriaLabel } from './format';
import './calendarGrid.scss';

const WEEKDAY_LABELS = [ 'Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa' ];
const ARROW_AND_EDGE_KEYS = [ 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End' ];

const isSelected = (state: string): boolean =>
  state === 'range-start' || state === 'range-end' || state === 'range-single' || state === 'in-range';

// El día tabulable por defecto: preferir un extremo del rango si cae en este
// panel, si no el día 1 — nunca uno de los días de relleno fuera de mes.
const defaultActiveDay = (panelMonth: YearMonth, range: DateRange): LocalDate => {
  if(monthOf(range.start) === panelMonth) {
    return range.start;
  }
  if(monthOf(range.end) === panelMonth) {
    return range.end;
  }
  return `${panelMonth}-01` as LocalDate;
};

// Un solo calendario para un solo rango — A y B nunca coinciden en el mismo
// grid (§07). Tabindex rotatorio: un único día tabulable por grid (§10). Las
// flechas que salen del mes navegan el panel en vez de aterrizar en una
// celda deshabilitada — nunca queda el foco donde Enter no hace nada.
const CalendarGrid = ({ panelMonth, range, axis, onDayClick, onDayHover, onNavigateMonth }: Props) => {
  const weeks = generateMonthGrid(panelMonth);
  const [ activeDay, setActiveDay ] = useState<LocalDate>(() => defaultActiveDay(panelMonth, range));
  const focusPendingRef = useRef(false);
  const gridRef = useRef<HTMLDivElement>(null);

  // Si panelMonth cambia por una vía ajena al teclado de este grid (dropdown,
  // flecha del header, tira de continuación), reancla el cursor si ya no
  // cae en el mes visible. No dispara si el propio teclado ya lo actualizó.
  useEffect(() => {
    if(monthOf(activeDay) !== panelMonth) {
      setActiveDay(defaultActiveDay(panelMonth, range));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ panelMonth ]);

  useEffect(() => {
    if(!focusPendingRef.current || monthOf(activeDay) !== panelMonth) {
      return;
    }
    gridRef.current?.querySelector<HTMLButtonElement>(`[data-day="${activeDay}"]`)?.focus();
    focusPendingRef.current = false;
  }, [ activeDay, panelMonth ]);

  const moveTo = (target: LocalDate) => {
    const targetMonth = monthOf(target);
    if(targetMonth !== panelMonth) {
      onNavigateMonth(targetMonth < panelMonth ? -1 : 1);
    }
    focusPendingRef.current = true;
    setActiveDay(target);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if(ARROW_AND_EDGE_KEYS.includes(event.key)) {
      event.preventDefault();
      moveTo(resolveKeyboardTarget(activeDay, event.key as NavKey));
      return;
    }
    if(event.key === 'PageUp' || event.key === 'PageDown') {
      event.preventDefault();
      moveTo(addMonthsClamped(activeDay, event.key === 'PageUp' ? -1 : 1));
      return;
    }
    if(event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onDayClick(activeDay);
    }
  };

  return (
    <div
      className={`rdp-grid ${axis === 'B' ? 'rdp-grid--b' : ''}`}
      role="grid"
      ref={gridRef}
      onKeyDown={onKeyDown}
      onMouseLeave={() => onDayHover(null)}
    >
      <div className="rdp-grid__weekdays" role="row">
        {
          WEEKDAY_LABELS.map(label => (
            <span key={label} className="rdp-grid__weekday" role="columnheader">{label}</span>
          ))
        }
      </div>
      {
        weeks.map((week, index) => (
          <div className="rdp-grid__row" role="row" key={index}>
            {
              week.map(day => {
                const state = resolveCellState(day, panelMonth, range);
                const disabled = state === 'outside-month';
                return (
                  <button
                    key={day}
                    type="button"
                    role="gridcell"
                    data-day={day}
                    aria-disabled={disabled}
                    aria-selected={isSelected(state)}
                    aria-label={formatCellAriaLabel(day, state, axis)}
                    tabIndex={day === activeDay ? 0 : -1}
                    className={`rdp-cell rdp-cell--${state}`}
                    onClick={() => !disabled && onDayClick(day)}
                    onFocus={() => !disabled && setActiveDay(day)}
                    onMouseEnter={() => !disabled && onDayHover(day)}
                  >
                    {Number(day.slice(-2))}
                  </button>
                );
              })
            }
          </div>
        ))
      }
    </div>
  );
}

interface Props {
  panelMonth: YearMonth,
  range: DateRange,
  axis: 'A' | 'B',
  onDayClick: (day: LocalDate) => void,
  onDayHover: (day: LocalDate | null) => void,
  onNavigateMonth: (delta: number) => void
}

export { CalendarGrid };
